import { FC, ReactNode, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { OnboardingCreateForm } from 'src/sections/onboarding/onboarding-create-form';
import { StateCookie } from 'src/cookie/cookie';
import { useSelector } from 'src/store';
import { useGetTeams } from 'src/hooks/use-get-teams';
import logger from 'src/log/log';

type TeamAccountGuardProps = {
  children: ReactNode;
};

export const TeamAccountGuard: FC<TeamAccountGuardProps> = ({ children }) => {
  const [isMounted, setIsMounted] = useState(false);
  const navigate = useNavigate();
  const params = useParams();
  const { hasFetched, isLoading, team } = useGetTeams();
  const currentTeam = useSelector((state) => state.team.current);
  const haveTeam = team.listTeams.length > 0;

  // Log current state
  logger.debug('🛡️ TEAM GUARD: State check', {
    isMounted,
    hasFetched,
    isLoading,
    haveTeam,
    teamsCount: team.listTeams?.length || 0,
    currentTeam: currentTeam?.id || null,
    currentTeamName: currentTeam?.name || null,
    paramTeamId: params.teamId,
    url: window.location.pathname,
  });

  useEffect(() => {
    setIsMounted(true);

    // If no teamId in params, try to get from cookie or first team, then navigate
    if (isMounted && hasFetched && !isLoading && haveTeam && !params.teamId) {
      logger.debug('🛡️ TEAM GUARD: No teamId in params, resolving team ID');
      let teamId = new StateCookie().getLastTeamId();
      logger.debug('🍪 TEAM GUARD: Got teamId from cookie:', teamId);

      if (!teamId && team.listTeams && team.listTeams.length > 0) {
        teamId = team.listTeams[0].id;
        logger.debug('⚠️ TEAM GUARD: No cookie teamId, using first team:', {
          teamId,
          teamName: team.listTeams[0].name,
        });
      }

      if (teamId) {
        // Always navigate to team overview, not settings page
        const targetUrl = `/t/${teamId}/`;
        logger.debug('🧭 TEAM GUARD: Navigating to resolved team overview (avoiding settings):', {
          teamId,
          targetUrl,
          teamsAvailable: team.listTeams?.map((t) => ({ id: t?.id, name: t?.name })) || [],
        });
        navigate(targetUrl, { replace: true });
      } else {
        logger.debug('❌ TEAM GUARD: No team ID could be resolved for navigation');
      }
    }

    return () => setIsMounted(false);
  }, [isMounted, hasFetched, isLoading, haveTeam, params.teamId, navigate]);

  if (!isMounted) {
    logger.debug('🛡️ TEAM GUARD: Not mounted yet, returning null');
    return null;
  }

  if (!haveTeam && hasFetched && !isLoading) {
    logger.debug('🛡️ TEAM GUARD: No teams found, showing onboarding', {
      haveTeam,
      hasFetched,
      isLoading,
    });
    return <OnboardingCreateForm />;
  }

  logger.debug('🛡️ TEAM GUARD: Rendering children (dashboard)', {
    haveTeam,
    hasFetched,
    isLoading,
    currentTeam: currentTeam?.name || 'none',
  });
  return <>{children}</>;
};
